import useChatTopics, { useTopicMessageState, useTopicModeReload } from '../../components/common/chat/topics/useChatTopics';
import { ChatTopicsProvider } from '../../components/common/chat/topics/ChatTopicsContext';
// D:\Casticart\frontend\src\components\admin\chats\ChatPage.js
import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/Sidebar';
import '../../css/admin/chats/ChatPage.css';
import { useAuth } from '../../contexts/AuthContext';
import { useMessageCount } from '../../contexts/MessageCountContext';
import { useChatUnreadDivider } from '../../components/common/chat/timeline/chatUnreadDivider';
import { useInView } from 'react-intersection-observer';

// 共通コンポーザー
import { useMaintenance, MAINTENANCE_MODES } from '../../contexts/MaintenanceContext';
import useChatMentionTaskResolution from '../../components/common/chat/mentions/useChatMentionTaskResolution';
import useChatProfileModalState from '../../components/common/chat/members/useChatProfileModalState';
import useChatAnnouncements from '../../components/common/chat/announcements/useChatAnnouncements';
import useChatMessageReactions from '../../components/common/chat/reactions/useChatMessageReactions';
import useChatNotesBadgeCount from '../../components/common/chat/notes/useChatNotesBadgeCount';

// トースト
import { useToast } from '../../components/common/ToastSystem';

// ★ 追加：共通ローダー

// ★ 追加：共通DnDフック/オーバーレイ/CSS
import { clearActiveChatContext, setActiveChatContext } from '../../utils/activeChatContext';
import '../../css/common/chat/ChatDragDrop.css';
import { useAdminChatFollowupSummary } from '../../components/common/chat/followups/chatFollowupApi';
import { resolveAdminPermission } from '../../constants/adminPermissionCatalog';
import {
  buildChatMentionCandidates,
  normalizeMentionLabel,
  normalizeMentionTargetId,
  normalizeMentionTargetRole,
  normalizeMentionTargetType,
} from '../../components/common/chat/mentions/chatMentionUtils';
import { listAdminMentionCandidates, listAdminPendingMentionTargets, resolveAdminMentionTarget } from '../../api/adminChat';
import { buildAdminPersonalNotesListUrl, ADMIN_ARTIST_PINNED_TARGETS_STORAGE_KEY, normalizeArtistId, normalizePinnedArtistIds, isSameArtistId } from '../../components/admin/chats/chatPageHelpers';
import useAdminChatMessageSupport from './useAdminChatPageElement.message';
import useAdminChatRuntimeSupport from './useAdminChatPageElement.runtime';
import AdminChatPageView from './useAdminChatPageElement.view';
import { shouldIgnorePopStateForHistoryDismiss } from '../common/useHistoryDismiss';






export default function useAdminChatPageElement() {
  const ACTIVE_CHAT_OWNER = 'admin:personal-chat';
  const mobileBackButtonClassName = 'chat-header-back-btn';
  const { artistId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, fetchSession, checking } = useAuth();
  const {
    adminMessageCount,
    adminArtistChatTone,
    adminGroupChatMessageCount,
    adminGroupChatTone,
    setAdminArtistChatTone,
    optimisticallyClearAdminPersonalUnread,
    refreshAdminPersonalUnread,
  } = useMessageCount();

  const { error: toastError, success: toastSuccess } = useToast();
  const { maintenance, maintenanceMode } = useMaintenance();
  const isReadOnly =
    maintenanceMode === MAINTENANCE_MODES.READ_ONLY || (maintenance && maintenance.readOnly);
  const initialArtistChatTargetId = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      return normalizeArtistId(
        artistId ||
          params.get('artistId') ||
          params.get('artist') ||
          params.get('artist_id') ||
          ''
      );
    } catch {
      return normalizeArtistId(artistId);
    }
  }, [artistId, location.search]);
  const hasInitialStoredArtistTarget = useMemo(() => {
    try {
      return Boolean(sessionStorage.getItem('selectedArtistId'));
    } catch {
      return false;
    }
  }, []);
  const hasInitialArtistChatTarget = Boolean(initialArtistChatTargetId || hasInitialStoredArtistTarget);
  const adminPermissions = useMemo(() => {
    if (Array.isArray(user?.permissions)) return user.permissions;
    return user?.permissions ? [user.permissions] : [];
  }, [user?.permissions]);
  const adminRole = user?.role || 'general';
  const loggedInAdminId = String(user?.userId ?? user?.id ?? user?.adminUserId ?? user?.admin_user_id ?? '').trim();
  const isExternalAccount = user?.is_external_account === true;
  const canViewArtistTargets = useMemo(
    () =>
      resolveAdminPermission({
        role: adminRole,
        isExternalAccount,
        permissions: adminPermissions,
        permissionKey: 'NAV_PERSONAL_CHAT',
      }),
    [adminPermissions, adminRole, isExternalAccount]
  );
  const canUseGroupChat = useMemo(
    () =>
      resolveAdminPermission({
        role: adminRole,
        isExternalAccount,
        permissions: adminPermissions,
        permissionKey: 'NAV_GROUP_CHAT',
      }),
    [adminPermissions, adminRole, isExternalAccount]
  );
  const canViewPersonalChatForSession = useCallback(
    (sessionUser) =>
      resolveAdminPermission({
        role: sessionUser?.role || 'general',
        isExternalAccount: sessionUser?.is_external_account === true,
        permissions: Array.isArray(sessionUser?.permissions)
          ? sessionUser.permissions
          : sessionUser?.permissions
            ? [sessionUser.permissions]
            : [],
        permissionKey: 'NAV_PERSONAL_CHAT',
      }),
    []
  );
  const [personalMentionAdmins, setPersonalMentionAdmins] = useState([]);
  const [pendingMentionTargets, setPendingMentionTargets] = useState({});
  const [resolvingMentionTargetId, setResolvingMentionTargetId] = useState('');

  const personalMentionCandidates = useMemo(
    () =>
      buildChatMentionCandidates({
        admins: personalMentionAdmins,
        includeArtists: false,
        includeParts: false,
        currentUser: { role: 'admin', id: loggedInAdminId },
      }),
    [loggedInAdminId, personalMentionAdmins],
  );
  const personalMentionCandidateLookup = useMemo(() => {
    const lookup = new Map();
    personalMentionCandidates.forEach((candidate) => {
      if (candidate?.type === 'person' && candidate?.role && candidate?.id) {
        lookup.set(`${candidate.role}:${candidate.id}`, candidate);
      }
    });
    return lookup;
  }, [personalMentionCandidates]);

  const refreshPendingMentionTargets = useCallback(async () => {
    if (!canViewArtistTargets) return;
    try {
      const data = await listAdminPendingMentionTargets({ scope: 'personal' });
      const next = {};
      (Array.isArray(data?.targets) ? data.targets : []).forEach((target) => {
        const id = normalizeArtistId(target?.targetId);
        if (!id) return;
        next[id] = {
          count: Number(target?.count || 0),
          messageId: target?.messageId || target?.message_id || null,
          latestCreatedAt: target?.latestCreatedAt || null,
        };
      });
      setPendingMentionTargets(next);
    } catch {
      setPendingMentionTargets({});
    }
  }, [canViewArtistTargets]);

  const resolveMentionTarget = useCallback(async (targetId) => {
    const normalizedTargetId = normalizeArtistId(targetId);
    if (!normalizedTargetId) return;
    setResolvingMentionTargetId(normalizedTargetId);
    try {
      await resolveAdminMentionTarget({ scope: 'personal', targetId: normalizedTargetId });
      setPendingMentionTargets((current) => {
        const next = { ...current };
        delete next[normalizedTargetId];
        return next;
      });
    } catch {
      toastError('メンション対応状態の更新に失敗しました。');
    } finally {
      setResolvingMentionTargetId('');
    }
  }, [toastError]);

  const updatePendingMentionTargetCount = useCallback((scope, targetId, delta) => {
    if (scope !== 'personal') return;
    const normalizedTargetId = normalizeArtistId(targetId);
    if (!normalizedTargetId) return;
    setPendingMentionTargets((current) => {
      const currentTarget = current[normalizedTargetId];
      const nextCount = Math.max(0, Number(currentTarget?.count || 0) + Number(delta || 0));
      const next = { ...current };
      if (nextCount <= 0) {
        delete next[normalizedTargetId];
      } else {
        next[normalizedTargetId] = { ...currentTarget, count: nextCount };
      }
      return next;
    });
  }, []);

  const {
    modalProps: mentionResolutionModalProps,
    loadingTargetKey: loadingMentionResolutionTargetKey,
    openMentionResolution,
  } = useChatMentionTaskResolution({
    actorType: 'admin',
    onTargetCountChange: updatePendingMentionTargetCount,
    onError: toastError,
  });

  useEffect(() => {
    if (!canViewArtistTargets) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await listAdminMentionCandidates();
        if (!cancelled) setPersonalMentionAdmins(Array.isArray(data?.admins) ? data.admins : []);
      } catch {
        if (!cancelled) setPersonalMentionAdmins([]);
      }
    })();
    refreshPendingMentionTargets();
    return () => {
      cancelled = true;
    };
  }, [canViewArtistTargets, refreshPendingMentionTargets]);
  const canViewOfferSummary = useMemo(
    () =>
      resolveAdminPermission({
        role: adminRole,
        isExternalAccount,
        permissions: adminPermissions,
        permissionKey: 'NAV_CASTING_OFFER',
      }),
    [adminPermissions, adminRole, isExternalAccount]
  );
  const canViewContractSummary = useMemo(
    () =>
      resolveAdminPermission({
        role: adminRole,
        isExternalAccount,
        permissions: adminPermissions,
        permissionKey: 'NAV_CASTING_CONTRACT',
      }),
    [adminPermissions, adminRole, isExternalAccount]
  );
  const canViewArtistProfileBasic = useMemo(
    () =>
      resolveAdminPermission({
        role: adminRole,
        isExternalAccount,
        permissions: adminPermissions,
        permissionKey: 'ARTIST_VIEW_PROFILE_BASIC',
      }),
    [adminPermissions, adminRole, isExternalAccount]
  );
  const canViewPersonalNotes = useMemo(
    () =>
      resolveAdminPermission({
        role: adminRole,
        isExternalAccount,
        permissions: adminPermissions,
        permissionKey: 'PERSONAL_CHAT_VIEW_NOTES',
      }),
    [adminPermissions, adminRole, isExternalAccount]
  );
  const canManagePersonalNotes = useMemo(
    () =>
      resolveAdminPermission({
        role: adminRole,
        isExternalAccount,
        permissions: adminPermissions,
        permissionKey: 'PERSONAL_CHAT_EDIT_NOTES',
      }),
    [adminPermissions, adminRole, isExternalAccount]
  );

  const sidebarRef = useRef(null);
  const [sidebarEl, setSidebarEl] = useState(null);
  const artistTargetAccessDeniedRef = useRef(false);
  const artistTargetsRetryTimerRef = useRef(null);

  // ======== 追加：モバイル表示制御 ========
  // isMobile: ビューポートが700px以下
  // mobileView: 'list'（アーティスト一覧全画面）|'chat'（チャット全画面）|null（PC）
  const initialMobileView = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const isNarrow = window.matchMedia('(max-width: 700px)').matches;
    if (!isNarrow) return null;
    if (hasInitialArtistChatTarget) return 'chat';
    try {
      if (sessionStorage.getItem('selectedArtistId')) return 'chat';
    } catch {}
    return 'list';
  }, [hasInitialArtistChatTarget]);
  const pendingRestoreRef = useRef(initialMobileView === 'chat');
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 700px)').matches);
  const [mobileView, setMobileView] = useState(initialMobileView);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const listOnly = isMobile && mobileView === 'list';
    window.dispatchEvent(
      new CustomEvent('casticart:chat-footer', { detail: { listOnly, chatMode: 'artist' } })
    );
  }, [isMobile, mobileView]);

  const handleOpenGroupChatFromTargetList = useCallback(() => {
    navigate('/admin/workspaces/communications/group-chat', {
      replace: true,
      state: { reset: Date.now(), chatType: 'group' },
    });
  }, [navigate]);

  // 画面幅監視
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 700px)');
    const onChange = (e) => {
      setIsMobile(e.matches);
      if (!e.matches) {
        // PC化したら通常2ペインに戻す
        pendingRestoreRef.current = false;
        setMobileView(null);
      } else {
        if (pendingRestoreRef.current && !selectedArtistIdRef.current) {
          setMobileView('chat');
          return;
        }
        if (mobileListPrefRef.current) {
          setMobileView('list');
          return;
        }
        // モバイル化時：選択済みならチャットを優先、未選択なら一覧
        const hasSelection = !!(selectedArtistIdRef.current || isAiTargetSelectedRef.current);
        setMobileView(hasSelection ? 'chat' : 'list');
      }
    };
    onChange(mql);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const goBackMobile = useCallback(() => {
    // 履歴があれば戻る。無い場合は一覧へフォールバック
    if (window.history.length > 1) {
      window.history.back();
    } else {
      mobileListPrefRef.current = true;
      mobileChatHistoryRef.current = false;
      setMobileView('list');
    }
  }, []);

  // 自動追加
  const [allowAutoAppend, setAllowAutoAppend] = useState(true);
  const pendingArtistIdsRef = useRef(new Set());

  const [artists, setArtists] = useState([]);
  const [parts, setParts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [genres, setGenres] = useState([]);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [isAiTargetSelected, setIsAiTargetSelected] = useState(() => !hasInitialArtistChatTarget);
  const [followupRefreshKey, setFollowupRefreshKey] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const selectedArtistId = selectedArtist?.artist_id || null;
  const isAiTargetSelectedRef = useRef(!hasInitialArtistChatTarget);
  const {
    summary: followupSummary,
    badgeCount: followupBadgeCount,
  } = useAdminChatFollowupSummary();
  const {
    profileTarget,
    closeProfileModal,
    openAdminProfile,
    openArtistProfile,
  } = useChatProfileModalState();
  const handleMentionClick = useCallback((mention = {}) => {
    if (normalizeMentionTargetType(mention) !== 'person') return;
    const role = normalizeMentionTargetRole(mention);
    const targetId = normalizeMentionTargetId(mention);
    const label = normalizeMentionLabel(mention?.label || mention?.displayLabel);
    if (!targetId) return;
    if (role === 'admin') {
      const candidate = personalMentionCandidateLookup.get(`admin:${targetId}`) || {};
      openAdminProfile({
        id: targetId,
        admin_user_id: targetId,
        display_name: candidate.label || label || '管理者',
        full_name: candidate.label || label || '',
        profile_picture_url: candidate.avatarUrl || '',
        position: candidate.subtitle || '',
      });
      return;
    }
    if (role === 'artist') {
      openArtistProfile({
        artist_id: targetId,
        artist_name: label || 'アーティスト',
      });
    }
  }, [openAdminProfile, openArtistProfile, personalMentionCandidateLookup]);
  const [offerAnnouncements, setOfferAnnouncements] = useState([]);
  const {
    announcements,
    latestAnnouncement,
    announcementCount,
    listOpen: isAnnouncementListOpen,
    setListOpen: setIsAnnouncementListOpen,
    refreshAnnouncements,
    pinAnnouncement,
    unpinAnnouncement,
    isAnnouncedMessage,
    actionMessageId: announcementActionMessageId,
  } = useChatAnnouncements({
    mode: 'admin',
    scope: 'individual',
    artistId: selectedArtistId || '',
    enabled: Boolean(selectedArtistId && !isAiTargetSelected),
    onError: toastError,
  });
  const { noteCount, refreshNoteCount } = useChatNotesBadgeCount({
    ownerId: selectedArtistId,
    buildListUrl: buildAdminPersonalNotesListUrl,
    enabled: Boolean(selectedArtistId && !isAiTargetSelected && canViewPersonalNotes),
  });
  useEffect(() => {
    if (!canViewPersonalNotes && showNotes) {
      setShowNotes(false);
    }
  }, [canViewPersonalNotes, showNotes]);
  const [contractAnnouncements, setContractAnnouncements] = useState([]);
  const [isSidePanelLoading, setIsSidePanelLoading] = useState(false);
  const [artistDetail, setArtistDetail] = useState(null);
  const [artistDetailLoading, setArtistDetailLoading] = useState(false);
  const isPersonalChatAreaOpen = Boolean(
    selectedArtistId &&
      !isAiTargetSelected &&
      !showNotes &&
      (!isMobile || mobileView === 'chat'),
  );
  const isPersonalChatAreaOpenRef = useRef(false);

  const chatTopics = useChatTopics({ scope: 'individual', conversationId: selectedArtistId, role: 'admin', accountId: user?.userId, enabled: !isAiTargetSelected });

  const [messages, setMessages] = useTopicMessageState(chatTopics);
  // 「ここから未読」の境界。管理者の個人チャットは相手ごとなので、一覧行の未読件数を使う。
  const unreadDividerMessageId = useChatUnreadDivider({
    conversationKey: `direct:admin:${selectedArtistId || ''}`,
    unreadCount: Number(selectedArtist?.unread_messages || 0),
    messages,
  });

  const [replyDraft, setReplyDraft] = useState(null);
  const {
    detailOpen: isReactionDetailOpen,
    detailGroups: reactionDetailGroups,
    detailLoading: reactionDetailLoading,
    openReactionDetail,
    closeReactionDetail,
    toggleReaction,
    refreshMessageReactionDetails,
    isReactionPending,
  } = useChatMessageReactions({
    mode: 'admin',
    scope: 'individual',
    artistId: selectedArtistId || '',
    enabled: Boolean(selectedArtistId && !isAiTargetSelected),
    setMessages,
    onError: toastError,
  });
  const messagesRef = useRef([]);
  const [oldestCursor, setOldestCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [hasNewerMessages, setHasNewerMessages] = useState(false);
  const [loadingNewerMessages, setLoadingNewerMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [messageWindowOffset, setMessageWindowOffset] = useState(0);
  const [activeAnchorMessageId, setActiveAnchorMessageId] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [loadedMessagesArtistId, setLoadedMessagesArtistId] = useState('');

  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsArtist, setSettingsArtist] = useState(null);
  const [isNicknameAllowed, setIsNicknameAllowed] = useState(false);
  const [isReadNotifyEnabled, setIsReadNotifyEnabled] = useState(false);
  const [readEnabledAdmins, setReadEnabledAdmins] = useState([]);
  const [readNotifyFetching, setReadNotifyFetching] = useState(false);
  const [readToggleLoading, setReadToggleLoading] = useState(false);
  const [readOptInVersion, setReadOptInVersion] = useState(0);
  const [artistChatMuted, setArtistChatMuted] = useState(false);
  const [artistChatMuteLoading, setArtistChatMuteLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [messageMenu, setMessageMenu] = useState(null);
  const [targetMenu, setTargetMenu] = useState(null);
  const [quickTaskSource, setQuickTaskSource] = useState(null);
  const [inlineCopyMessageId, setInlineCopyMessageId] = useState('');
  const imageModalHistoryRef = useRef({ pushed: false, closing: false });

  const [partFilter, setPartFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [messageFilter, setMessageFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [pinnedArtistIds, setPinnedArtistIds] = useState(() => {
    try {
      if (typeof window === 'undefined') return [];
      const raw = window.localStorage.getItem(ADMIN_ARTIST_PINNED_TARGETS_STORAGE_KEY);
      return raw ? normalizePinnedArtistIds(JSON.parse(raw)) : [];
    } catch {
      return [];
    }
  });
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchDraft, setSearchDraft] = useState({
    name: '',
    part: '',
    group: '',
    message: '',
  });

  const [showNewMsgBanner, setShowNewMsgBanner] = useState(false);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(true);
  const flyerCacheRef = useRef(new Map());
  const [, setFlyerCacheVersion] = useState(0);
  const reminderCacheRef = useRef(new Map());
  const [, setReminderCacheVersion] = useState(0);
  const longPressTimerRef = useRef(null);
  const sidebarPressRef = useRef({
    suppressClick: false,
    suppressContextMenu: false,
    timer: null,
    contextMenuTimer: null,
  });

  // ★ 外枠（スクロールしないコンテナ＝ハイライト対象）
  const chatContainerOuterRef = useRef(null);
  // ▼ メッセージリスト（スクロールする領域＝ドロップ判定）
  const chatContainerRef = useRef(null);

const bannerTimeoutRef = useRef(null);
const reconcileTimerRef = useRef(null);
const initialSelectionDoneRef = useRef(false);
const lastDiffFetchRef = useRef(Date.now());
const isMessageSearchModeRef = useRef(false);
const readMarkInFlightRef = useRef(new Map());
const readMarkCompletedAtRef = useRef(new Map());
const STALE_MS = 60 * 60 * 1000; // 60分超ならフルリロード
const READ_MARK_DEDUP_WINDOW_MS = 1000;
const mobileListPrefRef = useRef(initialMobileView === 'list'); // モバイルで一覧を優先表示するユーザー操作があったか
const mobileChatHistoryRef = useRef(false); // モバイルチャット用の履歴エントリを push 済みか
const prevSelectedArtistIdRef = useRef(null);
const anchorHighlightTimerRef = useRef(null);

  // モバイルの戻る操作＋画像モーダルの戻る操作
  useEffect(() => {
    const onPop = (event) => {
      if (shouldIgnorePopStateForHistoryDismiss(event)) return;
      if (imageModalHistoryRef.current.closing) {
        imageModalHistoryRef.current.closing = false;
        imageModalHistoryRef.current.pushed = false;
        return;
      }
      if (isModalOpen) {
        imageModalHistoryRef.current.pushed = false;
        setIsModalOpen(false);
        return;
      }
      if (!isMobile) return;
      mobileListPrefRef.current = true;
      mobileChatHistoryRef.current = false;
      setMobileView('list');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [isMobile, isModalOpen]);

  // ====== 送信中ローダー ======
  const [sendingBusy, setSendingBusy] = useState(false);

  // 現在選択中の artist_id
  const selectedArtistIdRef = useRef(null);
  const selectedArtistRef = useRef(null);
  const fetchInitialMessagesRef = useRef(null);
  const settingsArtistIdRef = useRef(null);
  const settingsArtistRef = useRef(null);
  const settingsOpenAllowsReadRef = useRef(false);
  const userRef = useRef(null);
  const genresByIdRef = useRef(new Map());
  const genresByValueLowerRef = useRef(new Map());
  useEffect(() => {
    isAiTargetSelectedRef.current = isAiTargetSelected;
  }, [isAiTargetSelected]);

  useEffect(() => {
    const currentId = selectedArtist?.artist_id || null;
    selectedArtistIdRef.current = currentId;
    selectedArtistRef.current = selectedArtist;

    if (currentId && pendingRestoreRef.current) {
      pendingRestoreRef.current = false;
    }

    if (currentId && currentId !== prevSelectedArtistIdRef.current) {
      // 新しいアーティストを選択したら、一覧優先フラグをリセット
      mobileListPrefRef.current = false;
      prevSelectedArtistIdRef.current = currentId;
      mobileChatHistoryRef.current = false;
    }
  }, [selectedArtist]);

  useEffect(() => {
    isPersonalChatAreaOpenRef.current = isPersonalChatAreaOpen;
  }, [isPersonalChatAreaOpen]);

  useEffect(() => {
    if (isPersonalChatAreaOpen) {
      setActiveChatContext({
        topicId: chatTopics.topicId || chatTopics.draftTopic?.clientId || '',
        owner: ACTIVE_CHAT_OWNER,
        scope: 'admin',
        channel: 'personal',
        artistId: selectedArtistId,
      });
      return () => clearActiveChatContext(ACTIVE_CHAT_OWNER);
    }
    clearActiveChatContext(ACTIVE_CHAT_OWNER);
    return () => clearActiveChatContext(ACTIVE_CHAT_OWNER);
  }, [isPersonalChatAreaOpen, selectedArtistId]);

  const shouldShowSidePanel = !isAiTargetSelected && Boolean(selectedArtist);
  const isSidePanelLayoutOpen = shouldShowSidePanel && isSidePanelOpen;

  useEffect(() => {
    const currentId = settingsArtist?.artist_id || null;
    settingsArtistIdRef.current = currentId;
    settingsArtistRef.current = settingsArtist;
  }, [settingsArtist]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    setInlineCopyMessageId('');
  }, [selectedArtistId]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);
  useEffect(() => {
    isMessageSearchModeRef.current = !!String(messageFilter || '').trim();
  }, [messageFilter]);

  // モバイルでターゲット選択済みかつ一覧優先フラグが立っていない場合にのみ、自動でチャットビューへ切り替える
  useEffect(() => {
    if (!isMobile) return;
    if (pendingRestoreRef.current) return;

    const hasSelection = !!(selectedArtistIdRef.current || isAiTargetSelectedRef.current);
    if (!hasSelection) {
      if (mobileView !== 'list') {
        setMobileView('list');
      }
      return;
    }

    // 一覧優先フラグが立っている状態で、何らかの操作により chat ビューになった場合は
    // 「ユーザーが明示的にチャットを開いた」とみなしてフラグを解除する
    if (mobileView === 'chat' && mobileListPrefRef.current) {
      mobileListPrefRef.current = false;
      return;
    }

    if (mobileListPrefRef.current) {
      if (mobileView !== 'list') {
        setMobileView('list');
      }
    } else if (mobileView !== 'chat') {
      setMobileView('chat');
    }
  }, [isMobile, mobileView, selectedArtist, isAiTargetSelected]);

  // モバイルでチャットビューを開いたときは、ブラウザバックで一覧に戻れるように履歴エントリを 1 回だけ追加
  useEffect(() => {
    if (!isMobile) {
      mobileChatHistoryRef.current = false;
      return;
    }
    if (
      mobileView === 'chat' &&
      (selectedArtistIdRef.current || isAiTargetSelectedRef.current) &&
      !mobileListPrefRef.current &&
      !mobileChatHistoryRef.current
    ) {
      try {
        window.history.pushState({ mobileChat: true }, '', '');
        mobileChatHistoryRef.current = true;
      } catch {
        // history.pushState が使えない環境では何もしない
        mobileChatHistoryRef.current = false;
      }
    }
  }, [isMobile, mobileView, isAiTargetSelected]);
  const readOptInCacheRef = useRef(new Map());
  const artistMuteCacheRef = useRef(new Map());
  const bumpReadOptInVersion = useCallback(
    () => setReadOptInVersion((v) => v + 1),
    [],
  );

  // messages pagination
  const limit = 20;

  // artists pagination
  const ARTISTS_PAGE_SIZE = 20;
  const [artistOffset, setArtistOffset] = useState(0);
  const [artistsHasMore, setArtistsHasMore] = useState(true);
  const [artistsLoading, setArtistsLoading] = useState(false);
  const loadedArtistIdsRef = useRef(new Set());
  const artistsFetchSeqRef = useRef(0);
  const artistsLoadingRef = useRef(false);
  const pendingReadArtistIdsRef = useRef(new Map());

  const { ref: artistsSentinelRef, inView: artistsSentinelInView } = useInView({
    root: sidebarEl || null,
    rootMargin: '400px 0px',
    triggerOnce: false,
  });
  const swipeStateRef = useRef(null);
  const prunePendingReadArtists = useCallback(() => {
    const now = Date.now();
    for (const [artistId, expiresAt] of pendingReadArtistIdsRef.current.entries()) {
      if (!Number.isFinite(expiresAt) || expiresAt <= now) {
        pendingReadArtistIdsRef.current.delete(artistId);
      }
    }
  }, []);
  const markArtistPendingRead = useCallback((artistId) => {
    const normalizedArtistId = normalizeArtistId(artistId);
    if (!normalizedArtistId) return;
    pendingReadArtistIdsRef.current.set(normalizedArtistId, Date.now() + 1500);
  }, []);
  const clearArtistPendingRead = useCallback((artistId) => {
    const normalizedArtistId = normalizeArtistId(artistId);
    if (!normalizedArtistId) return;
    pendingReadArtistIdsRef.current.delete(normalizedArtistId);
  }, []);
  const shouldSuppressArtistUnread = useCallback((artistId) => {
    if (!chatTopics.canClearParentUnread()) return false;
    const normalizedArtistId = normalizeArtistId(artistId);
    if (!normalizedArtistId) return false;
    prunePendingReadArtists();
    return (
      pendingReadArtistIdsRef.current.has(normalizedArtistId) ||
      (
        isPersonalChatAreaOpenRef.current &&
        isSameArtistId(normalizedArtistId, selectedArtistRef.current?.artist_id)
      )
    );
  }, [prunePendingReadArtists]);
  const normalizeArtistSidebarRow = useCallback((artistRow) => {
    if (!artistRow) return artistRow;
    if (!shouldSuppressArtistUnread(artistRow.artist_id)) return artistRow;
    return {
      ...artistRow,
      unread_messages: 0,
    };
  }, [shouldSuppressArtistUnread]);

  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return;
    const enable = () => setAllowAutoAppend(true);
    el.addEventListener('scroll', enable, { passive: true, once: true });
    el.addEventListener('wheel', enable, { passive: true, once: true });
    el.addEventListener('touchstart', enable, { passive: true, once: true });
    return () => {
      el.removeEventListener('scroll', enable);
      el.removeEventListener('wheel', enable);
      el.removeEventListener('touchstart', enable);
    };
  }, []);

  const {
    formatSidebarTimestamp, getArtistLatestTimestamp, getSidebarPreviewText, isSystemFormat, canUnsendMessage, canAnnouncementMessage,
    canReactMessage, canReplyMessage, canCopyMessage, canCreateQuickTaskFromMessage, messageMenuRef, targetMenuRef,
    closeMessageMenu, closeTargetMenu, openTargetMenuAt, clearMessageLongPress, handleMessageContextMenu, handleMessageTouchStart,
    handleUnsendMessage, handleToggleAnnouncementMessage, handleMessageMenuReactionSelect, handleCopyMessage, handleOpenInlinePartialCopy, handleOpenQuickTaskFromMessage,
    renderPartialCopyContent, handleSelectReplyMessage, handleSelectAnnouncementMessage, handleJumpToReplyMessage, hasInlineReminderSchedule, ensureReminderSchedule,
    parseReminderContent, pickFlyerFilename, ensureFlyerUrl, getCachedFlyerUrl, formatMessageContent, extractFirstUrl,
    getHoldDeclineLink, getMessageClass, resolvePrimaryGenreObj, getGenreLabel, getGenreColor, getGenreTextColor,
    getGenreLabelFromCache, getProfileImageStyle, openSelectedArtistProfile, isViewingLatest, scrollToLatest, scrollToTop,
    scrollSidebarToTop, fetchNicknamePermissionStatus, fetchReadReceiptSettings, fetchArtistMuteStatus, markMessagesAsRead, saveLast,
    mapMessagesForDisplay, fetchInitialMessages, fetchOlderMessages, loadNewerMessages, parseTs, ensureArtistInList,
  } = useAdminChatMessageSupport({
    chatTopics,
    ARTISTS_PAGE_SIZE, Error, READ_MARK_DEDUP_WINDOW_MS, activeAnchorMessageId, allowAutoAppend, anchorHighlightTimerRef,
    artistDetail, artistId, artistMuteCacheRef, artistOffset, artistTargetAccessDeniedRef, artistTargetsRetryTimerRef,
    artists, artistsFetchSeqRef, artistsHasMore, artistsLoading, artistsLoadingRef, artistsSentinelInView,
    assigneeFilter, bumpReadOptInVersion, canViewArtistTargets, canViewPersonalChatForSession, chatContainerRef, checking,
    closeProfileModal, fetchInitialMessagesRef, fetchSession, flyerCacheRef, genres, genresByIdRef,
    genresByValueLowerRef, groupFilter, hasMore, hasNewerMessages, initialSelectionDoneRef, inlineCopyMessageId,
    isAnnouncedMessage, isFetching, isMobile, isReadOnly, limit, loadedArtistIdsRef,
    loadingNewerMessages, localStorage, location, longPressTimerRef, messageFilter, messageMenu,
    messageWindowOffset, messages, mobileChatHistoryRef, mobileListPrefRef, mobileView, nameFilter,
    navigate, normalizeArtistSidebarRow, oldestCursor, openArtistProfile, partFilter, pendingArtistIdsRef,
    pendingRestoreRef, pinAnnouncement, pinnedArtistIds, prevSelectedArtistIdRef, readMarkCompletedAtRef, readMarkInFlightRef,
    readOptInCacheRef, reconcileTimerRef, reminderCacheRef, selectedArtist, selectedArtistId, selectedArtistIdRef,
    selectedArtistRef, sessionStorage, setActiveAnchorMessageId, setArtistChatMuteLoading, setArtistChatMuted, setArtistOffset,
    setArtists, setArtistsHasMore, setArtistsLoading, setAssigneeFilter, setFlyerCacheVersion, setGenres,
    setGroups, setHasMore, setHasNewerMessages, setInlineCopyMessageId, setIsAiTargetSelected, setIsAnnouncementListOpen,
    setIsFetching, setIsInitialLoading, setIsNicknameAllowed, setIsReadNotifyEnabled, setLoadedMessagesArtistId, setLoadingNewerMessages,
    setLoadingOlderMessages, setMessageMenu, setMessageWindowOffset, setMessages, setMobileView, setOldestCursor,
    setParts, setQuickTaskSource, setReadEnabledAdmins, setReadNotifyFetching, setReminderCacheVersion, setReplyDraft, setFollowupRefreshKey,
    setSelectedArtist, setTargetMenu, settingsArtistIdRef, sidebarRef, targetMenu, toastError,
    toastSuccess, toggleReaction, unpinAnnouncement, user,
  });

  const {
    assigneeQuickToggles, displayedArtists, isPinnedArtist, handleTogglePinnedFromTargetMenu, handleArtistClick, openAiInbox,
    handleOpenAiTask, handleGoArtistDetail, handleOpenSettingsFromTargetMenu, handleSettingsButtonClick, clearSidebarLongPress, clearSidebarContextMenuBlock,
    handleSidebarTouchStart, handleReadNotifyToggle, handleArtistMuteToggle, imageMessages, imagePreviewUrls, imageFullUrls,
    imageModalItems, imageIndexByMessageId, displayMessages, handleImageClick, closeModal, showPrevImage,
    showNextImage, announcementSummaries, handleDownload, searchSummaryLabel, clearToolbarSearch, searchToolbarChips,
    isTargetSearchMode, showDelayedInitialLoading, shouldShowChatGuidance, openTargetSearchModal, applyTargetSearchModal, clearTargetSearchModal,
    handleBannerClick, handleNicknamePermissionChange, handleComposerSent, handleUploading, isDragActive, overlayRect,
    rootClasses, closeChatSettingsModal,
  } = useAdminChatRuntimeSupport({
    chatTopics,
    Error, STALE_MS, adminArtistChatTone, adminMessageCount, artistMuteCacheRef, artists,
    assigneeFilter, bannerTimeoutRef, bumpReadOptInVersion, canViewArtistProfileBasic, canViewContractSummary, canViewOfferSummary,
    chatContainerOuterRef, chatContainerRef, clearArtistPendingRead, closeTargetMenu, contractAnnouncements,
    ensureArtistInList, ensureFlyerUrl, ensureReminderSchedule, fetchArtistMuteStatus, fetchInitialMessages, fetchNicknamePermissionStatus,
    fetchOlderMessages, fetchReadReceiptSettings, flyerCacheRef, getArtistLatestTimestamp, getGenreLabelFromCache, getMessageClass,
    goBackMobile, groupFilter, groups, hasInlineReminderSchedule, hasMore, hasNewerMessages,
    imageModalHistoryRef, isAiTargetSelected, isFetching, isInitialLoading, isMessageSearchModeRef, isMobile,
    isPersonalChatAreaOpen, isPersonalChatAreaOpenRef, isReadOnly, isSystemFormat, isViewingLatest, lastDiffFetchRef,
    loadNewerMessages, loadedMessagesArtistId, loadingNewerMessages, loadingOlderMessages, mapMessagesForDisplay, markArtistPendingRead,
    markMessagesAsRead, messageFilter, messages, messagesRef, mobileChatHistoryRef, mobileListPrefRef,
    mobileView, nameFilter, navigate, offerAnnouncements, openTargetMenuAt, optimisticallyClearAdminPersonalUnread,
    parseInt, parseTs, partFilter, pinnedArtistIds, readOptInCacheRef, readOptInVersion,
    reconcileTimerRef, refreshAdminPersonalUnread, refreshAnnouncements, refreshMessageReactionDetails, refreshPendingMentionTargets, reminderCacheRef,
    replyDraft, saveLast, scrollSidebarToTop, scrollToLatest, scrollToTop, searchDraft,
    selectedArtist, selectedArtistId, selectedArtistIdRef, selectedArtistRef, setActiveAnchorMessageId, setAdminArtistChatTone,
    setArtistChatMuteLoading, setArtistChatMuted, setArtistDetail, setArtistDetailLoading, setArtists, setAssigneeFilter,
    setContractAnnouncements, setCurrentImageIndex, setFollowupRefreshKey, setGroupFilter, setHasMore, setHasNewerMessages, setIsAiTargetSelected,
    setIsModalOpen, setIsNicknameAllowed, setIsReadNotifyEnabled, setIsSidePanelLoading, setMessageFilter, setMessageWindowOffset,
    setMessages, setMobileView, setNameFilter, setOfferAnnouncements, setPartFilter, setPinnedArtistIds,
    setReadEnabledAdmins, setReadToggleLoading, setReplyDraft, setSearchDraft, setSelectedArtist, setSendingBusy,
    setSettingsArtist, setShowNewMsgBanner, setShowSearchModal, setShowSettingsModal, settingsArtistIdRef, settingsArtistRef,
    settingsOpenAllowsReadRef, showSettingsModal, sidebarPressRef, swipeStateRef, targetMenu, toastError,
    toastSuccess, userRef,
  });
  useTopicModeReload(chatTopics, () => fetchInitialMessages(selectedArtistId), () => { setMessages([]); });

  if (!canViewArtistTargets) {
    return (
      <div className="admin-dashboard">
        <AdminSidebar />
        <div className="dashboard-main">
          <div className="chat-page">
            <div className="chat-empty">
              <div className="chat-empty-icon">
                <i className="fa-regular fa-circle-stop" aria-hidden="true" />
              </div>
              <h2>個別チャットの対象一覧を表示する権限がありません。</h2>
              <p>個別チャット権限が必要です。権限設定を確認してください。</p>
            </div>
          </div>
        </div>
      </div>
    );
  }


    return (
    <ChatTopicsProvider value={chatTopics}>
    <AdminChatPageView
      context={{
        chatTopics,        activeAnchorMessageId, adminGroupChatMessageCount, adminGroupChatTone, announcementActionMessageId, announcementCount, announcementSummaries, announcements, applyTargetSearchModal, artistChatMuteLoading, artistChatMuted,
        artistDetail, artistDetailLoading, artistsLoading, artistsSentinelRef, assigneeQuickToggles, canAnnouncementMessage, canCopyMessage, canCreateQuickTaskFromMessage,
        canManagePersonalNotes, canReactMessage, canReplyMessage, canSwitchToGroupChat: canUseGroupChat, canUnsendMessage, canViewPersonalNotes, chatContainerOuterRef, chatContainerRef, clearMessageLongPress,
        clearSidebarContextMenuBlock, clearSidebarLongPress, clearTargetSearchModal, clearToolbarSearch, closeChatSettingsModal, closeMessageMenu, closeModal, closeProfileModal,
        closeReactionDetail, closeTargetMenu, currentImageIndex, displayMessages, unreadDividerMessageId, displayedArtists, extractFirstUrl, followupBadgeCount, followupRefreshKey, followupSummary,
        formatMessageContent, formatSidebarTimestamp, getArtistLatestTimestamp, getCachedFlyerUrl, getGenreColor, getGenreLabel, getGenreTextColor,
        getHoldDeclineLink, getMessageClass, getProfileImageStyle, getSidebarPreviewText, goBackMobile, groups, handleArtistClick, handleArtistMuteToggle,
        handleBannerClick, handleComposerSent, handleCopyMessage, handleDownload, handleGoArtistDetail, handleImageClick, handleJumpToReplyMessage, handleMessageContextMenu,
        handleMentionClick, handleOpenGroupChatFromTargetList,
        handleMessageMenuReactionSelect, handleMessageTouchStart, handleNicknamePermissionChange, handleOpenAiTask, handleOpenInlinePartialCopy, handleOpenQuickTaskFromMessage, handleOpenSettingsFromTargetMenu, handleReadNotifyToggle,
        handleSelectAnnouncementMessage, handleSelectReplyMessage, handleSettingsButtonClick, handleSidebarTouchStart, handleToggleAnnouncementMessage, handleTogglePinnedFromTargetMenu, handleUnsendMessage, handleUploading,
        hasMore, imageFullUrls, imageIndexByMessageId, imageMessages, imageModalItems, imagePreviewUrls, inlineCopyMessageId, isAiTargetSelected,
        isAnnouncedMessage, isAnnouncementListOpen, isDragActive, isMobile, isModalOpen, isNicknameAllowed, isPinnedArtist, isReactionDetailOpen,
        isReactionPending, isReadNotifyEnabled, isReadOnly, isSidePanelLayoutOpen, isSidePanelLoading, isSidePanelOpen, isSystemFormat, isTargetSearchMode,
        latestAnnouncement, loggedInAdminId, loadingMentionResolutionTargetKey, loadingOlderMessages, mentionResolutionModalProps, messageFilter, messageMenu, messageMenuRef, mobileBackButtonClassName,
        mobileView, noteCount, openAiInbox, openMentionResolution, openReactionDetail, openSelectedArtistProfile, openTargetMenuAt, openTargetSearchModal,
        overlayRect, parseReminderContent, parts, pendingMentionTargets, personalMentionCandidates, pickFlyerFilename, profileTarget, quickTaskSource,
        reactionDetailGroups, reactionDetailLoading, readEnabledAdmins, readNotifyFetching, readOptInCacheRef, readToggleLoading, refreshNoteCount, renderPartialCopyContent,
        replyDraft, resolveMentionTarget, resolvePrimaryGenreObj, resolvingMentionTargetId, rootClasses, searchDraft, searchSummaryLabel, searchToolbarChips,
        selectedArtist, selectedArtistId, sendingBusy, setIsAnnouncementListOpen, setIsSidePanelOpen, setQuickTaskSource, setReplyDraft, setSearchDraft,
        setShowNotes, setShowSearchModal, setSidebarEl, settingsArtist, shouldShowChatGuidance, shouldShowSidePanel, shouldSuppressArtistUnread, showDelayedInitialLoading,
        showNewMsgBanner, showNextImage, showNotes, showPrevImage, showSearchModal, showSettingsModal, sidebarPressRef, sidebarRef,
        targetMenu, targetMenuRef, toastError, toastSuccess, toggleReaction, unpinAnnouncement,
      }}
    />
    </ChatTopicsProvider>
  );
}
